import React, {useRef, useEffect, useState} from 'react'

const getColor = (value, min, max) => {
  const ratio = (value - min) / (max - min)

  // Use a smoother color mapping with better transitions
  // Blue -> Cyan -> Green -> Yellow -> Orange -> Red
  let r, g, b

  if (ratio <= 0.2) {
    // Blue to Cyan (0-20%)
    const t = ratio / 0.2
    r = 0
    g = Math.round(255 * t)
    b = 255
  } else if (ratio <= 0.4) {
    // Cyan to Green (20-40%)
    const t = (ratio - 0.2) / 0.2
    r = 0
    g = 255
    b = Math.round(255 * (1 - t))
  } else if (ratio <= 0.6) {
    // Green to Yellow (40-60%)
    const t = (ratio - 0.4) / 0.2
    r = Math.round(255 * t)
    g = 255
    b = 0
  } else if (ratio <= 0.8) {
    // Yellow to Orange (60-80%)
    const t = (ratio - 0.6) / 0.2
    r = 255
    g = Math.round(255 * (1 - t * 0.5)) // Reduce green more slowly
    b = 0
  } else {
    // Orange to Red (80-100%)
    const t = (ratio - 0.8) / 0.2
    r = 255
    g = Math.round(255 * 0.5 * (1 - t))
    b = 0
  }

  return `rgb(${r},${g},${b})`
}

const formatFC = c => {
  if (typeof c === 'number' && !isNaN(c)) {
    const fahrenheit = (c * 9) / 5 + 32
    return `${fahrenheit.toFixed(1)}°F / ${c.toFixed(1)}°C`
  }
  return '--'
}

export const ThermalLegend = ({minimum, maximum, width = 400, height = 12}) => {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    if (typeof minimum !== 'number' || typeof maximum !== 'number' || !isFinite(minimum) || !isFinite(maximum)) return
    if (minimum === maximum) return

    const w = width
    const h = height
    canvas.width = w
    canvas.height = h

    const ctx = canvas.getContext('2d')
    const imageData = ctx.createImageData(w, h)

    for (let x = 0; x < w; x++) {
      const value = minimum + ((maximum - minimum) * x) / Math.max(w - 1, 1)
      const color = getColor(value, minimum, maximum)
      const [r, g, b] = color.match(/\d+/g).map(Number)

      for (let y = 0; y < h; y++) {
        const index = (y * w + x) * 4
        imageData.data[index + 0] = r
        imageData.data[index + 1] = g
        imageData.data[index + 2] = b
        imageData.data[index + 3] = 255
      }
    }

    ctx.putImageData(imageData, 0, 0)
  }, [minimum, maximum, width, height])

  if (typeof minimum !== 'number' || typeof maximum !== 'number') {
    return null
  }

  const mid = (minimum + maximum) / 2

  return (
    <div
      style={{
        marginTop: '8px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div
        style={{
          width: `${width}px`,
          maxWidth: '100%',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid #ccc',
          backgroundColor: '#000'
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: `${height}px`,
            display: 'block'
          }}
        />
      </div>
      <div
        style={{
          width: `${width}px`,
          maxWidth: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 4,
          fontSize: '0.75em',
          color: '#000'
        }}
      >
        <span>{formatFC(minimum)}</span>
        <span>{formatFC(mid)}</span>
        <span>{formatFC(maximum)}</span>
      </div>
    </div>
  )
}

const ThermalImage = ({temperatures, minimum, maximum, scale = 10, label = '', color = 'white'}) => {
  const canvasRef = useRef()
  const [tooltip, setTooltip] = useState(null)

  const rows = temperatures?.length || 24
  const cols = temperatures?.[0]?.length || 32

  const displayWidth = cols * scale
  const displayHeight = rows * scale

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    canvas.width = cols
    canvas.height = rows

    if (!temperatures || temperatures.length === 0) {
      // Draw light gray background
      ctx.fillStyle = '#ccc'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
    } else {
      const imageData = ctx.createImageData(cols, rows)

      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const temp = temperatures[y][x]
          const color = getColor(temp, minimum, maximum)
          const [r, g, b] = color.match(/\d+/g).map(Number)
          const index = (y * cols + x) * 4
          imageData.data[index + 0] = r
          imageData.data[index + 1] = g
          imageData.data[index + 2] = b
          imageData.data[index + 3] = 255
        }
      }

      ctx.putImageData(imageData, 0, 0)
    }
  }, [temperatures, minimum, maximum, cols, rows])

  const handleMouseMove = e => {
    if (!temperatures || temperatures.length === 0) return
    const rect = canvasRef.current.getBoundingClientRect()
    const xRatio = (e.clientX - rect.left) / rect.width
    const yRatio = (e.clientY - rect.top) / rect.height
    const x = Math.floor(xRatio * cols)
    const y = Math.floor(yRatio * rows)

    if (y >= 0 && y < temperatures.length && x >= 0 && x < temperatures[0].length) {
      const celsius = temperatures[y][x]
      const fahrenheit = (celsius * 9) / 5 + 32
      setTooltip({
        x: e.clientX - rect.left + 10,
        y: e.clientY - rect.top + 10,
        value: `${fahrenheit.toFixed(1)}°F / ${celsius.toFixed(1)}°C`
      })
    } else {
      setTooltip(null)
    }
  }

  const handleMouseLeave = () => {
    setTooltip(null)
  }

  return (
    <div
      style={{
        display: 'inline-block',
        padding: '2px',
        border: '1px solid #ccc',
        borderRadius: 4,
        backgroundColor: '#333',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.34)',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          position: 'relative',
          width: `${displayWidth}px`,
          maxWidth: '100%'
        }}
      >
        <canvas
          ref={canvasRef}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            width: '100%',
            height: 'auto',
            imageRendering: 'pixelated',
            display: 'block'
          }}
        />
        {(!temperatures || temperatures.length === 0) && (
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              right: '6px',
              color,
              fontSize: '0.85em',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px black',
              pointerEvents: 'none'
            }}
          >
            No thermal data available
          </div>
        )}
        {label && (
          <div
            style={{
              position: 'absolute',
              bottom: '4px',
              left: '6px',
              color,
              fontSize: '0.85em',
              fontWeight: 'bold',
              textShadow: '1px 1px 2px black',
              pointerEvents: 'none'
            }}
          >
            {label}
          </div>
        )}
        {tooltip && (
          <div
            style={{
              position: 'absolute',
              left: tooltip.x,
              top: tooltip.y,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              padding: '2px 5px',
              fontSize: '0.75em',
              borderRadius: '3px',
              pointerEvents: 'none'
            }}
          >
            {tooltip.value}
          </div>
        )}
      </div>
    </div>
  )
}

export default ThermalImage
